import { cn } from "../../libs/cn";
import { createEffect, createSignal, For, onMount, Show, splitProps } from "solid-js";
import { buttonVariants, Button } from "./button";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from 'dayjs/plugin/localizedFormat' // ES 2015

// Extend Day.js with required plugins
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);

const CalendarRoot = (props) => {
  const [local, others] = splitProps(props, ["class"]);
  return <div class={cn("p-2", props.class)} {...others} />;
};

const CalendarNav = (props) => {
  const [local, others] = splitProps(props, ["class"]);
  return <div class={cn("space-x-1 flex items-center w-full", props.class)} {...others} />;
};

const CalendarNavButton = (props) => {
  const [local, others] = splitProps(props, ["class", "type", "onClick"]);

  const navButtonCss = cn(
    buttonVariants({ variant: "outline" }),
    "h-7 w-7 flex-none bg-transparent p-0 opacity-50 hover:opacity-100",
    props.class
  );

  const content = () => {
    if (props.type === "prev") return <>&lt;</>;
    if (props.type === "next") return <>&gt;</>;
    return props.children;
  };

  return (
    <button class={navButtonCss} onClick={props.onClick} {...others}>
      {content()}
    </button>
  );
};

const CalenderNavCaption = (props) => {
  const [local, others] = splitProps(props, ["class"]);
  return (
    <span
      class={cn(
        "flex grow justify-center mx-2 relative items-center",
        props.class
      )}
      {...others}
    >
      <span class="text-sm font-medium">{props.children}</span>
    </span>
  );
};

const CalenderHeadRow = (props) => {
  const [local, others] = splitProps(props, ["class"]);
  return (
    <tr class={cn("flex w-full mt-1", props.class)} {...others}>
      {props.children}
    </tr>
  );
};

const CalenderHeadCell = (props) => {
  const [local, others] = splitProps(props, ["class"]);
  return (
    <th
      class={cn(
        "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
        props.class
      )}
      {...others}
    >
      {props.children}
    </th>
  );
};

const CalendarDayRow = (props) => {
  const [local, others] = splitProps(props, ["class"]);
  return (
    <tr class={cn("flex w-full mt-2", props.class)} {...others}>
      {props.children}
    </tr>
  );
};

const CalendarDayCell = (props) => {
  const [local, others] = splitProps(props, ["class"]);
  return (
    <td
      class={cn(
        "flex-1 p-0 text-center text-sm",
        "has-[[data-in-range]]:bg-accent has-[[data-in-range]]:first-of-type:rounded-l-md has-[[data-in-range]]:last-of-type:rounded-r-md",
        "has-[[data-range-end]]:rounded-r-md has-[[data-range-start]]:rounded-l-md",
        "has-[[data-outside-range][data-in-range]]:bg-accent/50",
        props.class
      )}
      {...others}
    >
      {props.children}
    </td>
  );
};

const CalendarDay = (props) => {
  const [local, others] = splitProps(props, ["class", "onClick", "current", "active", "inactive"]);
  return (
    <button
      data-current={props.current && !props.active}
      data-active={props.active}
      data-inactive={props.inactive && !props.active}
      data-inrange={props.range && !props.active}
      class={cn(
        buttonVariants({ variant: "ghost" }),
        "h-8 w-8 p-0 font-normal aria-selected:opacity-100",
        props.class,
        "data-[inactive=true]:text-gray-300",
        "data-[active=true]:bg-gray-900 data-[active=true]:text-white",
        "data-[current=true]:bg-accent",
        "data-[inrange=true]:bg-accent data-[inrange=true]:rounded-none",
      )}
      onClick={props.onClick}
      {...others}
    >
      {props.children}
    </button>
  );
};

const CalendarContent = (props) => {
  const [local, others] = splitProps(props, ["class", "dayViewCss", "view", "onYearClick", "onMonthClick"]);

  return (
    <div class={cn("w-full flex justify-center items-center mt-2", local.class)}>
      <Show when={local.view === "day"}>
        <DayView {...others} class={local.dayViewCss} />
      </Show>
      <Show when={local.view === "month"}>
        <MonthView {...others} onMonthClick={local.onMonthClick} class={local.monthViewCss} />
      </Show>
      <Show when={local.view === "year"}>
        <YearView {...others} onYearClick={local.onYearClick} class={local.yearViewCss} />
      </Show>
    </div>
  );
};

const MonthView = (props) => {
  return (
    <table class="border-collapse space-y-1">
      <For each={Array(3).fill(0)}>{(_, rowIndex) => (
        <tr class="flex w-full">
          <For each={Array(4).fill(0)}>{(_, colIndex) => {
            const month = (rowIndex() * 4) + colIndex();
            return (
              <td class="flex-1 p-0 text-center text-sm">
                <Button variant="ghost" class="text-sm w-[50px]" onClick={() => props.onMonthClick(month)}>
                  {dayjs().month(month).format("MMM")}
                </Button>
              </td>
            );
          }}</For>
        </tr>
      )}</For>
    </table>
  );
};

const YearView = (props) => {
  const activeYear = () => {
    const isEndYear = props.activeNav.year() % 25 === 0;
    return Math.floor(props.activeNav.year() / 25) * 25 - (isEndYear ? 25 : 0);
  }

  return (
    <table class="border-collapse space-y-1">
      <For each={Array(5).fill(activeYear())}>{(_, rowIndex) => (
        <tr class="flex w-full">
          <For each={Array(5).fill(activeYear())}>{(_, colIndex) => {
            const year = activeYear() + (rowIndex() * 5) + colIndex() + 1;
            return (
              <td class="flex-1 p-0 text-center text-sm">
                <Button variant="ghost" class="text-sm w-[50px]" onClick={() => props.onYearClick(year)}>
                  {dayjs().year(year).format("YYYY")}
                </Button>
              </td>
            );
          }}</For>
        </tr>
      )}</For>
    </table>
  );
};

const DayView = (props) => {
  const [local, others] = splitProps(props, ["class", "mode", "date", "endDate", "onDateClick", "activeNav"]);

  const weekDays = () => dayjs.weekdaysShort();

  const shouldStop = (date, endDate) => {
    if (props.mode !== "range") {
      return date.isAfter(endDate, "day");
    }

    return false;
  }

  const calendarWeeks = () => {
    const startOfMonth = local.activeNav.startOf("month");
    const endOfMonth = local.activeNav.endOf("month");

    const startDate = startOfMonth.startOf("week");
    const endDate = endOfMonth.endOf("week");

    const weeks = [];
    let date = startDate;
    let index = 0;
    while (!shouldStop(date, endDate) && index < 6) {
      const days = Array(7)
        .fill(0)
        .map(() => {
          const day = date;
          date = date.add(1, "day");
          return day;
        });
      weeks.push(days);
      index++;
    }
    return weeks;
  };

  const isActive = (date) => {
    if (props.mode === "range") {
      return date.isSame(props.date, "day") || date.isSame(props.endDate, "day");
    }

    return date.isSame(props.date, "day");
  }

  const isInRange = (date) => {
    if (props.mode !== "range") {
      return false;
    }

    return date.isAfter(props.date, "day") && date.isBefore(props.endDate, "day");
  }

  const handleDateClick = (date) => {
    props.onDateClick?.(date);
  };

  return (
    <table class={"border-collapse space-y-1"}>
      <CalenderHeadRow>
        <For each={weekDays()}>{(weekDay) => <CalenderHeadCell>{weekDay}</CalenderHeadCell>}</For>
      </CalenderHeadRow>
      <For each={calendarWeeks()}>{(week) => (
        <CalendarDayRow>
          <For each={week}>{(day) => (
            <CalendarDayCell>
              <CalendarDay
                current={day.isSame(dayjs(), "day")}
                active={isActive(day)}
                range={isInRange(day)}
                inactive={!day.isSame(local.activeNav, "month")}
                onClick={() => handleDateClick(day)}
              >
                {day.date()}
              </CalendarDay>
            </CalendarDayCell>
          )}</For>
        </CalendarDayRow>
      )}</For>
    </table>
  );
};

export const Calendar = (props) => {
  const [views, setViews] = createSignal([props.view ?? "day"]);
  const [navDates, setNavDates] = createSignal([props.date ?? dayjs()]);
  const [date, setDate] = createSignal(props.date ?? null);
  const [endDate, setEndDate] = createSignal(props.endDate ?? null);
  const [calCount, setCalCount] = createSignal(1);

  const navView = (index = 0) => {
    if (views()[index] === "day") {
      return 'month';
    }

    return 'year';
  }

  const navAmount = (index = 0) => {
    if (views()[index] === "year") {
      return 20;
    }

    return 1;
  }

  const setNavDate = (index, newDate) => {
    setNavDates(prev => {
      const newItems = [...prev];
      newItems[index] = newDate; // Modify the item
      return newItems;
    });
  }

  const setView = (index, newView) => {
    setViews(prev => {
      const newItems = [...prev];
      newItems[index] = newView; // Modify the item
      return newItems;
    });
  }

  const handlePrevNav = (index = 0) => {
    setNavDate(index, navDates()[index].subtract(navAmount(), navView()));
  }

  const handleNextNav = (index = 0) => {
    setNavDate(index, navDates()[index].add(navAmount(), navView()));
  }

  const onChange = (value) => {
    if (props.mode !== "range") {
      setDate(value);
      props.onChange?.(date());
      return;
    }

    if (date() && endDate()) {
      value.isBefore(date()) ? setDate(value) : setEndDate(value);
    }

    if (!date()) {
      setDate(value);
    }

    if (!endDate()) {
      setEndDate(value);
    }

    props.onChange?.({ startDate: date(), endDate: endDate() });
  }

  const onMonthClick = (month, index = 0) => {
    setNavDate(index, navDates()[index].month(month));
    setView(index, "day");
  }

  const onYearClick = (year, index = 0) => {
    setNavDate(index, navDates()[index].year(year));
    setView(index, "month");
  }

  createEffect(() => {
    props.date;
    setDate(props.date ?? null);
  });

  createEffect(() => {
    props.endDate;
    setEndDate(props.endDate ?? null);
  });

  createEffect(() => {
    props.mode;

    setNavDates([props.date ?? dayjs(), props.endDate ?? dayjs().add(1, "month")]);
    setViews([props.view ?? "day", props.view ?? "day"]);
    setCalCount(props.mode === "range" ? 2 : 1);
  });

  const caption = (index = 0) => {
    const navDate = navDates()[index];

    const view = views()[index];
    if (view === "day") {
      return navDate.format("MMMM YYYY");
    }

    if (view === "month") {
      return navDate.format("YYYY");
    }

    if (view === "year") {
      // 25 years range
      const isEndYear = navDate.year() % 25 === 0;
      const startYear = Math.floor(navDate.year() / 25) * 25 - (isEndYear ? 25 : 0);
      return `${startYear + 1} - ${startYear + 25}`;
    }
  }

  const changeView = (index = 0) => {
    const view = views()[index];
    if (view === "day") {
      setView(index, "month");
    } else if (view === "month") {
      setView(index, "year");
    }
  }

  return (
    <CalendarRoot class={props.class}>
      <div class="w-full flex gap-x-3">
        <For each={Array(calCount()).fill(0)}>{(_, index) => (
          <CalendarNav>
            <CalendarNavButton type="prev" onClick={() => handlePrevNav(index())} />
            <CalenderNavCaption>
              <Button onClick={() => changeView(index())} variant="ghost">{caption(index())}</Button>
            </CalenderNavCaption>
            <CalendarNavButton type="next" onClick={() => handleNextNav(index())} />
          </CalendarNav>
        )}</For>
      </div>
      <div class="w-full flex gap-x-3">
        <For each={Array(calCount()).fill(0)}>{(_, index) => (
          <CalendarContent
            mode={props.mode}
            view={views()[index()]}
            onYearClick={(year) => onYearClick(year, index())}
            onMonthClick={(month) => onMonthClick(month, index())}
            onDateClick={(value) => onChange(value)}
            date={date()}
            endDate={endDate()}
            activeNav={navDates()[index()]}
          />
        )}</For>
      </div>

    </CalendarRoot>
  );
};
